import React, { useState, useEffect, useRef } from 'react';
import { getProfileConfig, ProfileConfig } from '../config/profile';

type LineType = 'command' | 'output' | 'error' | 'info' | 'header' | 'dim' | 'empty' | 'help-entry';

interface OutputLine {
  type: LineType;
  text: string;
}

const PROMPT = 'visitor@anshuman:~$';
const COMMANDS = ['help', 'whoami', 'skills', 'experience', 'contact', 'clear'];

function helpOutput(): OutputLine[] {
  return [
    { type: 'header', text: 'Available commands:' },
    { type: 'empty', text: '' },
    { type: 'help-entry', text: 'whoami § Who is this person?' },
    { type: 'help-entry', text: 'skills § List all skills' },
    { type: 'help-entry', text: 'skills --tag <tag> § Filter skills by tag' },
    { type: 'help-entry', text: 'experience § Work history' },
    { type: 'help-entry', text: 'contact § Contact information' },
    { type: 'help-entry', text: 'clear § Clear the terminal' },
    { type: 'empty', text: '' },
    { type: 'dim', text: '  Tip: ↑/↓ for history, Tab to autocomplete' },
    { type: 'empty', text: '' },
  ];
}

function whoamiOutput(profile: ProfileConfig): OutputLine[] {
  return [
    { type: 'header', text: profile.name },
    { type: 'info', text: `  ${profile.title}` },
    { type: 'dim', text: `  ${profile.contact.location}` },
    { type: 'empty', text: '' },
  ];
}

function skillsOutput(profile: ProfileConfig, rawTag?: string): OutputLine[] {
  const tags = profile.skillTags.filter(t => t !== 'all');
  if (rawTag) {
    const matched = tags.find(t => t.toLowerCase().includes(rawTag.toLowerCase()));
    if (!matched) {
      return [
        { type: 'error', text: `  tag not found: "${rawTag}"` },
        { type: 'dim', text: `  Available tags: ${tags.join(', ')}` },
        { type: 'empty', text: '' },
      ];
    }
    const skills = profile.skills.filter(s =>
      s.tags.some(t => t.toLowerCase() === matched.toLowerCase())
    );
    return formatSkills(skills, `Skills — ${matched}`);
  }
  return [
    ...formatSkills(profile.skills, 'All Skills'),
    { type: 'dim', text: `  Filter: skills --tag <tag>  |  Tags: ${tags.join(', ')}` },
    { type: 'empty', text: '' },
  ];
}

function formatSkills(skills: ProfileConfig['skills'], heading: string): OutputLine[] {
  const lines: OutputLine[] = [
    { type: 'header', text: heading },
    { type: 'empty', text: '' },
  ];
  for (const skill of skills) {
    const filled = Math.round((skill.rating / 5) * 8);
    const bar = '█'.repeat(filled) + '░'.repeat(8 - filled);
    const name = skill.name.padEnd(28);
    const cats = skill.categoryTags.slice(0, 3).join(', ');
    lines.push({ type: 'output', text: `  ${name} [${bar}]  ${cats}` });
  }
  lines.push({ type: 'empty', text: '' });
  return lines;
}

function experienceOutput(profile: ProfileConfig): OutputLine[] {
  const lines: OutputLine[] = [
    { type: 'header', text: 'Work Experience' },
    { type: 'empty', text: '' },
  ];
  for (const exp of profile.experience) {
    lines.push({ type: 'info', text: `  [${exp.period}]  ${exp.title} @ ${exp.organization}` });
    lines.push({ type: 'dim', text: `  ${exp.location}` });
    lines.push({ type: 'output', text: `  ${exp.description}` });
    lines.push({ type: 'empty', text: '' });
    for (const a of exp.achievements.slice(0, 2)) {
      const truncated = a.length > 90 ? a.slice(0, 87) + '...' : a;
      lines.push({ type: 'dim', text: `  • ${truncated}` });
    }
    lines.push({ type: 'empty', text: '' });
  }
  return lines;
}

function contactOutput(profile: ProfileConfig): OutputLine[] {
  return [
    { type: 'header', text: 'Contact' },
    { type: 'empty', text: '' },
    { type: 'output', text: `  email     ${profile.contact.email}` },
    { type: 'output', text: `  linkedin  ${profile.contact.linkedin}` },
    { type: 'output', text: `  github    ${profile.contact.github}` },
    { type: 'output', text: `  location  ${profile.contact.location}` },
    { type: 'empty', text: '' },
  ];
}

const lineClass: Record<Exclude<LineType, 'help-entry'>, string> = {
  command: 'text-green-400',
  output: 'text-gray-300',
  error: 'text-red-400',
  info: 'text-cyan-400',
  header: 'text-yellow-400 font-semibold',
  dim: 'text-gray-500',
  empty: '',
};

interface TerminalWidgetProps {
  className?: string;
}

const TerminalWidget = ({ className = '' }: TerminalWidgetProps) => {
  const [output, setOutput] = useState<OutputLine[]>([]);
  const [input, setInput] = useState('');
  const [cmdHistory, setCmdHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [profile, setProfile] = useState<ProfileConfig | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getProfileConfig().then(setProfile);
    setOutput([
      { type: 'info', text: 'Portfolio Terminal — Anshuman Rohella' },
      { type: 'dim', text: "Type 'help' to see available commands." },
      { type: 'empty', text: '' },
    ]);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [output]);

  const processCommand = (cmd: string) => {
    const trimmed = cmd.trim();
    if (!trimmed) return;

    const parts = trimmed.split(/\s+/);
    const command = parts[0].toLowerCase();
    const newLines: OutputLine[] = [{ type: 'command', text: `${PROMPT} ${trimmed}` }];

    if (command === 'clear') {
      setOutput([]);
      setCmdHistory(h => [trimmed, ...h]);
      setHistoryIndex(-1);
      setInput('');
      return;
    }

    if (!profile) {
      newLines.push({ type: 'dim', text: 'Loading profile data...' }, { type: 'empty', text: '' });
    } else {
      switch (command) {
        case 'help':
          newLines.push(...helpOutput());
          break;
        case 'whoami':
          newLines.push(...whoamiOutput(profile));
          break;
        case 'skills': {
          const tagIdx = parts.indexOf('--tag');
          const tag = tagIdx !== -1 ? parts.slice(tagIdx + 1).join(' ') : undefined;
          newLines.push(...skillsOutput(profile, tag));
          break;
        }
        case 'experience':
          newLines.push(...experienceOutput(profile));
          break;
        case 'contact':
          newLines.push(...contactOutput(profile));
          break;
        default:
          newLines.push(
            { type: 'error', text: `  command not found: ${command}` },
            { type: 'dim', text: "  Type 'help' to see available commands." },
            { type: 'empty', text: '' }
          );
      }
    }

    setOutput(prev => [...prev, ...newLines]);
    setCmdHistory(h => [trimmed, ...h]);
    setHistoryIndex(-1);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processCommand(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(historyIndex + 1, cmdHistory.length - 1);
      setHistoryIndex(next);
      setInput(cmdHistory[next] ?? '');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.max(historyIndex - 1, -1);
      setHistoryIndex(next);
      setInput(next === -1 ? '' : cmdHistory[next]);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const match = COMMANDS.find(c => c.startsWith(input) && c !== input);
      if (match) setInput(match);
    }
  };

  return (
    <div
      className={`rounded-xl overflow-hidden shadow-2xl border border-gray-700 cursor-text ${className}`}
      onClick={() => inputRef.current?.focus()}
    >
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-800 border-b border-gray-700 select-none">
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <div className="w-3 h-3 rounded-full bg-yellow-400" />
        <div className="w-3 h-3 rounded-full bg-green-500" />
        <span className="ml-3 text-gray-400 text-xs font-mono">visitor@anshuman: ~</span>
      </div>

      {/* Output */}
      <div
        ref={scrollRef}
        className="bg-gray-900 p-4 h-80 overflow-y-auto font-mono text-sm leading-relaxed"
      >
        {output.map((line, i) => {
          if (line.type === 'help-entry') {
            const [cmd, desc] = line.text.split(' § ');
            return (
              <div key={i} className="flex gap-2">
                <span className="text-cyan-400 w-44 flex-shrink-0">{'  '}{cmd}</span>
                <span className="text-gray-600">│</span>
                <span className="text-gray-400">{desc}</span>
              </div>
            );
          }
          return (
            <div key={i} className={lineClass[line.type as Exclude<LineType, 'help-entry'>]}>
              {line.text || ' '}
            </div>
          );
        })}

        {/* Input line */}
        <div className="flex items-center text-green-400 mt-1">
          <span className="flex-shrink-0 mr-2 select-none">{PROMPT}</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent outline-none caret-green-400 text-green-400 min-w-0"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label="Terminal input"
          />
        </div>
      </div>
    </div>
  );
};

export default TerminalWidget;
